import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import {
  type Employee,
  EmployeeBadge,
  employeesQueryOptions,
  useSetEmployeeActiveMutation,
} from '@/entities/employee'
import { ChangePasswordForm } from '@/features/auth'
import { EmployeeDialog } from '@/features/team-editor'
import { TelegramStatus } from '@/features/telegram-link'
import { cn } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { Dialog } from '@/shared/ui/dialog'
import { Icon } from '@/shared/ui/icon'
import { Menu, MenuItem } from '@/shared/ui/menu'
import { PageBody } from '@/shared/ui/page-body'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { toast } from '@/shared/ui/toast'

export const Route = createFileRoute('/_app/team')({
  // Раздел администраторский целиком. Гейт здесь — вежливость к тому, кто
  // пришёл по ссылке или из закладки: список всё равно отдаст ему одного его
  // самого, а кнопки упрутся в отказ бэкенда.
  beforeLoad: ({ context }) => {
    if (!context.session.isAdmin) throw redirect({ to: '/schedule' })
  },
  component: TeamPage,
})

/**
 * Команда: администратор заводит людей, выдаёт пароли, раздаёт права и
 * отключает ушедших.
 */
function TeamPage() {
  const employeesQuery = useQuery(employeesQueryOptions(true))
  const setActive = useSetEmployeeActiveMutation()
  // null в editing — заведение нового; объект — правка. Диалог один.
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<Employee | null>(null)
  const [passwordFor, setPasswordFor] = useState<Employee | null>(null)

  const openEditor = (employee: Employee | null) => {
    setEditing(employee)
    setEditorOpen(true)
  }

  const employees = employeesQuery.data ?? []

  const toggleActive = (employee: Employee) => {
    setActive.mutate(
      { id: employee.id, active: !employee.active },
      {
        onSuccess: (result) => {
          if (!result.employee.active) {
            const hanging = result.futureTaskCount + result.futureExcursionCount
            toast.notify(
              hanging > 0
                ? `Отключён. В будущем на нём висит: дел ${result.futureTaskCount}, выходов ${result.futureExcursionCount}`
                : 'Отключён. Ничего в будущем на нём не висело',
            )
          }
        },
      },
    )
  }

  return (
    <PageBody>
      <PageHeader
        title="Команда"
        actions={
          <Button size="sm" onClick={() => openEditor(null)}>
            Завести сотрудника
          </Button>
        }
      />

      {employeesQuery.isLoading ? (
        <Spinner />
      ) : (
        <ul className="flex flex-col divide-y divide-border-subtle">
          {employees.map((employee) => (
            <li
              key={employee.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-control px-2 py-3 transition-colors hover:bg-bg-elevated"
            >
              {/* Отключённый приглушён целиком: он остаётся в списке ради
                  истории, но перестаёт спорить за внимание с работающими. */}
              <EmployeeBadge
                employee={employee}
                className={cn('min-w-0 flex-1', !employee.active && 'opacity-55')}
              />
              {/* На телефоне метки уходят под имя: «уведомления не доходят» в
                  одну строку с ним не помещается и срезает само имя, а имя в
                  списке команды — главное. */}
              <span className="order-last flex w-full flex-wrap items-center gap-2 sm:order-none sm:w-auto sm:shrink-0 sm:justify-end">
                {employee.isAdmin && <Chip variant="soft">админ</Chip>}
                {!employee.active && <Chip>отключён</Chip>}
                <TelegramStatus employee={employee} />
              </span>
              {/* Правка карточки — ежедневное, поэтому кнопкой. Пароль и
                  отключение делают раз в наём: в ряду они только шумят, а
                  красное «Отключить» в каждой строке кричит громче всего
                  раздела. */}
              <Button size="sm" variant="ghost" onClick={() => openEditor(employee)}>
                Изменить
              </Button>
              <Menu
                ariaLabel={`Ещё: ${employee.name}`}
                align="end"
                triggerClassName="px-1.5 py-1.5 text-text-tertiary hover:text-text-primary"
                trigger={<Icon name="more" size={18} />}
              >
                {(close) => (
                  <>
                    <MenuItem
                      onSelect={() => {
                        setPasswordFor(employee)
                        close()
                      }}
                    >
                      Выдать пароль
                    </MenuItem>
                    <MenuItem
                      onSelect={() => {
                        toggleActive(employee)
                        close()
                      }}
                      {...(employee.active ? { className: 'text-accent hover:text-accent' } : {})}
                    >
                      {employee.active ? 'Отключить' : 'Вернуть в команду'}
                    </MenuItem>
                  </>
                )}
              </Menu>
            </li>
          ))}
        </ul>
      )}

      <EmployeeDialog open={editorOpen} employee={editing} onClose={() => setEditorOpen(false)} />

      <Dialog
        open={passwordFor !== null}
        onClose={() => setPasswordFor(null)}
        title={`Пароль: ${passwordFor?.name ?? ''}`}
      >
        {passwordFor && (
          <div className="flex flex-col gap-4">
            <p className="text-sm">Пароль передаётся лично — продукт его никуда не отправляет.</p>
            <ChangePasswordForm employeeId={passwordFor.id} own={false} />
          </div>
        )}
      </Dialog>
    </PageBody>
  )
}
